import { CalendarPlus, X } from "lucide-react";
import {
  useGetList,
  useLocaleState,
  useNotify,
  useTranslate,
  useUpdate,
  type Identifier,
} from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { EventEditDialog } from "../events/EventDialogs";
import { useEventTypeLookup } from "../events/eventTypes";
import { formatEventDate } from "../events/formatEvent";
import { useCalendarSync } from "../events/useCalendarSync";
import { RecordPicker } from "../misc/RecordPicker";
import type { CrmEvent } from "../types";
import { usePartnerDeals } from "./usePartnerDeals";

const MAX_EVENTS = 100;
const NEWEST_FIRST = { field: "start_at", order: "DESC" } as const;

/**
 * « Événements » tab of a partner: the events it is staffed on directly, to
 * add or leave, and those of its opportunities.
 */
export const PartnerEvents = ({ partnerId }: { partnerId: Identifier }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "fr"] = useLocaleState();
  const lookupType = useEventTypeLookup();
  const [selectedId, setSelectedId] = useState<Identifier | null>(null);
  const { data: deals = [], isPending: isDealsPending } =
    usePartnerDeals(partnerId);
  const dealIds = deals.map((deal) => deal.id);
  const {
    data: directEvents = [],
    isPending: isDirectPending,
    refetch,
  } = useGetList<CrmEvent>("events", {
    pagination: { page: 1, perPage: MAX_EVENTS },
    sort: NEWEST_FIRST,
    filter: { "partner_ids@cs": `{${partnerId}}` },
  });
  const { data: dealEvents = [], isPending: isDealEventsPending } =
    useGetList<CrmEvent>(
      "events",
      {
        pagination: { page: 1, perPage: MAX_EVENTS },
        sort: NEWEST_FIRST,
        filter: { "deal_id@in": `(${dealIds.join(",")})` },
      },
      { enabled: dealIds.length > 0 },
    );
  const [update, { isPending: isSaving }] = useUpdate<CrmEvent>();
  const syncCalendar = useCalendarSync();

  const isPending =
    isDirectPending ||
    isDealsPending ||
    (dealIds.length > 0 && isDealEventsPending);
  const directIds = new Set(directEvents.map((event) => event.id));
  const events = [
    ...directEvents,
    ...dealEvents.filter((event) => !directIds.has(event.id)),
  ].sort((a, b) => b.start_at.localeCompare(a.start_at));
  const now = new Date();

  const setStaffed = (event: CrmEvent, isStaffed: boolean) => {
    const partnerIds = event.partner_ids ?? [];
    update(
      "events",
      {
        id: event.id,
        data: {
          partner_ids: isStaffed
            ? [...partnerIds, partnerId]
            : partnerIds.filter((id) => id !== partnerId),
        },
        previousData: event,
      },
      {
        onSuccess: () => {
          refetch();
          syncCalendar({ event_id: event.id });
        },
        onError: () =>
          notify("resources.partners.link_error", { type: "error" }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3 pt-2">
      <div className="flex justify-end">
        <RecordPicker<CrmEvent>
          resource="events"
          label={translate("resources.partners.add_to_event")}
          icon={<CalendarPlus className="size-4" />}
          searchPlaceholder={translate("resources.partners.search_event")}
          emptyText={translate("crm.filters.no_match")}
          excludedIds={[...directIds]}
          sort={NEWEST_FIRST}
          disabled={isSaving}
          renderOption={(event) => (
            <span className="flex min-w-0 flex-col">
              <span className="truncate">{event.name}</span>
              <span className="text-xs text-muted-foreground">
                {formatEventDate(event, locale)}
              </span>
            </span>
          )}
          onSelect={(event) => setStaffed(event, true)}
        />
      </div>
      {isPending ? null : events.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.partners.no_events")}
        </p>
      ) : (
        <ul className="divide-y">
          {events.map((event) => {
            const type = lookupType(event.type);
            const isPast = new Date(event.end_at ?? event.start_at) < now;
            const deal = deals.find((item) => item.id === event.deal_id);
            const isDirect = directIds.has(event.id);
            return (
              <li key={event.id} className="flex items-center gap-3 py-2">
                <button
                  type="button"
                  onClick={() => setSelectedId(event.id)}
                  className="flex flex-1 min-w-0 items-start gap-2 rounded-sm text-left hover:bg-muted cursor-pointer"
                >
                  <span
                    className="mt-1.5 size-2 rounded-full shrink-0"
                    style={{ backgroundColor: type.color }}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={cn(
                        "text-sm font-medium truncate",
                        isPast && "text-muted-foreground",
                      )}
                    >
                      {event.name}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {formatEventDate(event, locale)}
                      {type.label ? ` · ${type.label}` : ""}
                      {!isDirect && deal
                        ? ` · ${translate("resources.partners.via_deal", { name: deal.name })}`
                        : ""}
                    </span>
                  </span>
                </button>
                {isDirect ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={isSaving}
                    aria-label={translate("resources.partners.remove_from", {
                      name: event.name,
                    })}
                    onClick={() => setStaffed(event, false)}
                  >
                    <X className="size-4" />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      <EventEditDialog
        eventId={selectedId}
        onClose={() => {
          setSelectedId(null);
          refetch();
        }}
      />
    </div>
  );
};
